import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function Eyebrow({ children }: { children: ReactNode }) {
  return <span className="eyebrow">{children}</span>;
}

export function PageHeader({
  eyebrow,
  title,
  lead,
  right,
}: {
  eyebrow: string;
  title: string;
  lead?: string;
  right?: ReactNode;
}) {
  return (
    <div className="mb-8 flex flex-col gap-5 border-b border-line pb-7 md:flex-row md:items-end md:justify-between">
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mt-4 font-display text-[30px] font-semibold leading-[1.1] md:text-[36px]">{title}</h1>
        {lead ? <p className="mt-3 max-w-[62ch] text-[15px] text-ink-soft">{lead}</p> : null}
      </div>
      {right ? <div className="flex flex-wrap gap-3">{right}</div> : null}
    </div>
  );
}

export function StatGrid({ children, cols = 3 }: { children: ReactNode; cols?: 2 | 3 | 4 }) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 border-l border-t border-line",
        cols === 2 && "sm:grid-cols-2",
        cols === 3 && "sm:grid-cols-2 lg:grid-cols-3",
        cols === 4 && "sm:grid-cols-2 lg:grid-cols-4",
      )}
    >
      {children}
    </div>
  );
}

export function Stat({
  value,
  label,
  tone = "steel",
  sub,
}: {
  value: ReactNode;
  label: string;
  tone?: "steel" | "signal";
  sub?: string;
}) {
  return (
    <div className="border-b border-r border-line bg-paper-raised p-6">
      <div
        className={cn(
          "font-mono text-[30px] font-semibold leading-none",
          tone === "signal" ? "text-signal-deep" : "text-steel",
        )}
      >
        {value}
      </div>
      <div className="mt-2 text-[13px] text-ink-soft">{label}</div>
      {sub ? <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.08em] text-steel-mid">{sub}</div> : null}
    </div>
  );
}

export function Panel({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("border border-line bg-paper-raised", className)}>
      <header className="flex items-center justify-between gap-3 border-b border-line px-[18px] py-[13px]">
        <h2 className="font-mono text-[12px] uppercase tracking-[0.1em] text-steel">{title}</h2>
        {action}
      </header>
      {children}
    </section>
  );
}

/** The signature inverted "live" board from the marketing site. */
export function Board({
  title,
  foot,
  children,
  live = true,
}: {
  title: string;
  foot?: string;
  children: ReactNode;
  live?: boolean;
}) {
  return (
    <section className="border border-steel bg-steel text-paper">
      <header className="flex items-center justify-between gap-3 border-b border-paper/20 px-[18px] py-[14px]">
        <span className="font-mono text-[12px] uppercase tracking-[0.1em]">{title}</span>
        {live ? (
          <span className="inline-flex items-center gap-[7px] font-mono text-[11px] tracking-[0.08em] text-signal">
            <i className="live-pulse inline-block h-[7px] w-[7px] bg-signal" />
            LIVE
          </span>
        ) : null}
      </header>
      {children}
      {foot ? (
        <footer className="border-t border-paper/20 px-[18px] py-3 font-mono text-[11px] text-paper/60">{foot}</footer>
      ) : null}
    </section>
  );
}

export function Button({
  variant = "primary",
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "ghost" | "signal" }) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex items-center justify-center border-2 border-steel px-5 py-[10px] text-[14px] font-semibold transition-colors disabled:opacity-50",
        variant === "primary" && "bg-steel text-paper hover:bg-steel-deep",
        variant === "ghost" && "bg-transparent text-steel hover:bg-paper-raised",
        variant === "signal" && "border-signal-deep bg-signal text-steel hover:bg-signal-deep hover:text-paper",
        className,
      )}
    />
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel-mid">{label}</span>
      <div className="mt-2">{children}</div>
      {error ? (
        <span role="alert" className="mt-1 block font-mono text-[11px] uppercase tracking-[0.08em] text-signal-deep">
          {error}
        </span>
      ) : hint ? (
        <span className="mt-1 block text-[12px] text-ink-soft">{hint}</span>
      ) : null}
    </label>
  );
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        "w-full border border-line-strong bg-paper px-3 py-[10px] font-mono text-[14px] text-ink outline-none placeholder:font-normal placeholder:italic placeholder:text-steel-mid/55 focus:border-steel focus:ring-2 focus:ring-steel/30",
        props.className,
      )}
    />
  );
}
